/**
 * Hooks React Query pour les consentements utilisateur
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { consentsClient } from '../api';
import type {
  GrantConsentDto,
  ConsentKind,
  ListConsentsDto,
} from '@sphere/user/domain';

const QUERY_KEY = ['user', 'consents'];

/**
 * Hook pour récupérer tous les consentements
 */
export function useConsents(filters?: ListConsentsDto) {
  return useQuery({
    queryKey: [...QUERY_KEY, filters],
    queryFn: () => consentsClient.getAll(filters),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook pour vérifier un consentement
 */
export function useConsentCheck(kind: ConsentKind, version?: string) {
  return useQuery({
    queryKey: [...QUERY_KEY, 'check', kind, version],
    queryFn: () => consentsClient.check(kind, version),
    enabled: !!kind,
  });
}

/**
 * Hook pour récupérer l'historique d'un consentement
 */
export function useConsentHistory(kind: ConsentKind) {
  return useQuery({
    queryKey: [...QUERY_KEY, 'history', kind],
    queryFn: () => consentsClient.getHistory(kind),
    enabled: !!kind,
  });
}

/**
 * Hook pour accorder/refuser un consentement
 */
export function useGrantConsent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: GrantConsentDto) => consentsClient.grant(dto),
    onSuccess: () => {
      // Invalider toutes les queries de consentements pour forcer un refetch
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Hook pour retirer un consentement
 */
export function useWithdrawConsent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ kind, version }: { kind: ConsentKind; version: string }) =>
      consentsClient.withdraw(kind, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Hook pour supprimer tous les consentements
 */
export function useDeleteAllConsents() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => consentsClient.deleteAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Helper pour vérifier si un consentement est accordé
 */
export function useHasConsent(kind: ConsentKind): boolean {
  const { data } = useConsents();
  const consent = data?.find((c) => c.kind === kind && !c.withdrawnAt);
  return consent?.isGranted ?? false;
}
